import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Send, Loader2, Mail, Radio, Gift, Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CinematicVideo } from "@/components/CinematicVideo";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DERIVE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/f5doupcy_gemini_generated_video_c99516e8.mp4";
const POSTER_DERIVE = "https://static.prod-images.emergentagent.com/jobs/08c324fb-59b2-4fff-a725-0bafcdd9ead9/images/313b7784d4804d7a3b4e0cbb7e0a29791f513d88ee7b543395f8264e1a8a727b.png";
const FEATURED_EP = { slug: "ia-intelligents", title: { fr: "L'IA nous rend-elle plus intelligents ?", en: "Does AI make us smarter?", es: "¿La IA nos hace más inteligentes?", nl: "Maakt AI ons slimmer?" } };
const LISTEN_LABEL = { fr: "Écouter", en: "Listen", es: "Escuchar", nl: "Luisteren" };

export function Newsletter() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const lng = (i18n.language || "fr").slice(0, 2);

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      toast.error(t("landing.newsletter.invalid"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: value, lang: i18n.language });
      toast.success(data.already ? t("landing.newsletter.already") : data.pending ? t("landing.newsletter.confirm") : t("landing.newsletter.success"));
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("landing.newsletter.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative px-6 py-14 sm:py-24" data-testid="newsletter-section">
      <motion.div
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
        className="max-w-2xl mx-auto glass stats-wow rounded-3xl px-7 py-12 text-center"
      >
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <Mail className="text-primary" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-3">{t("landing.newsletter.eyebrow")}</p>
        <h2 className="font-display text-gradient-orange text-3xl sm:text-4xl italic mb-6">{t("landing.newsletter.title")}</h2>

        <div className="max-w-lg mx-auto mb-8">
          <CinematicVideo silver src={DERIVE_VIDEO} testId="derive-cinematic-video" poster={POSTER_DERIVE} caption={t("landing.newsletter.subtitle")} fade noSound />
        </div>

        <div className="max-w-lg mx-auto mb-8 flex items-center justify-center gap-3 flex-wrap" data-testid="newsletter-featured-audio">
          <button onClick={toggleAudio} data-testid="newsletter-audio-btn"
            className="btn-frost-orange inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm flex-shrink-0">
            {playing ? <Pause size={15} /> : <Play size={15} />} {playing ? "Pause" : (LISTEN_LABEL[lng] || LISTEN_LABEL.fr)}
          </button>
          <span className="font-display italic text-foreground/80 text-sm">{FEATURED_EP.title[lng] || FEATURED_EP.title.fr}</span>
          <audio ref={audioRef} src={`/derive/${FEATURED_EP.slug}.mp3`} preload="none" onEnded={() => setPlaying(false)} data-testid="newsletter-audio-el" />
        </div>

        <div data-testid="newsletter-leadmagnet" className="max-w-lg mx-auto mb-6 inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-primary/25 bg-primary/[0.06]">
          <Gift size={15} className="text-primary flex-shrink-0" />
          <span className="text-sm text-foreground/80">{t("landing.newsletter.leadMagnet")}</span>
        </div>

        <form onSubmit={submit} noValidate className="flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto">
          <input
            type="text"
            inputMode="email"
            data-testid="newsletter-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("landing.newsletter.placeholder")}
            className="flex-1 rounded-full bg-black/30 border border-white/12 px-5 py-3.5 text-sm text-foreground placeholder:text-foreground/35 outline-none focus:border-primary/50 transition-colors"
          />
          <button
            type="submit"
            data-testid="newsletter-submit-btn"
            disabled={loading}
            className="btn-frost-orange inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm whitespace-nowrap disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {t("landing.newsletter.button")}
          </button>
        </form>
        <Link to="/derive" data-testid="newsletter-podcast-link"
          className="mt-6 inline-flex items-center gap-2 text-sm text-primary/80 hover:text-primary transition-colors">
          <Radio size={15} /> {t("landing.newsletter.podcast")}
        </Link>
      </motion.div>
    </section>
  );
}
